/**
 * Feature Configuration
 *
 * This file contains feature flags for enabling/disabling various features
 * across the application. This is particularly useful for gradual rollouts
 * and preventing frontend components from calling backend endpoints that
 * don't exist yet.
 */

const isEnabled = (value: string | undefined): boolean => value?.toLowerCase() === 'true';

export const FEATURE_FLAGS = {
  /**
   * Billing Dashboard Feature
   *
   * When enabled, the full billing dashboard with Stripe integration will be available.
   * This includes:
   * - Billing history and invoices
   * - Payment method management
   * - Usage metrics tracking
   * - Plan upgrade/downgrade flows
   *
   * Set to true to enable full billing functionality with Stripe sandbox.
   */
  BILLING_DASHBOARD: true,

  /**
   * Settings Page
   *
   * Enable the new comprehensive settings page with tabs for
   * account, billing, API keys, security, notifications, and appearance.
   */
  SETTINGS_PAGE: true,

  /**
   * Plan Upgrade Modal
   *
   * Enable the plan upgrade/downgrade modal with proration preview.
   * Requires billing endpoints to be available.
   */
  PLAN_UPGRADE_MODAL: false,

  /**
   * Knowledge Workspace V2
   *
   * Enables the unified knowledge library workspace across
   * documents, links, text, Q&A, and audio routes.
   *
   * Controlled by NEXT_PUBLIC_KNOWLEDGE_WORKSPACE_V2 for staged rollout.
   */
  KNOWLEDGE_WORKSPACE_V2:
    (process.env.NEXT_PUBLIC_KNOWLEDGE_WORKSPACE_V2 || 'false').toLowerCase() === 'true',

  /**
   * Links Quick Flow V2
   *
   * Enables the guided quick-add link trainer inside the knowledge links tab.
   *
   * Controlled by NEXT_PUBLIC_KNOWLEDGE_LINKS_QUICKFLOW_V2 for staged rollout.
   */
  KNOWLEDGE_LINKS_QUICKFLOW_V2: isEnabled(process.env.NEXT_PUBLIC_KNOWLEDGE_LINKS_QUICKFLOW_V2),

  /**
   * Live Data Connectors
   *
   * Enables the Connectors provider tab inside the Integrations workspace
   * (customer REST APIs feeding chat answers live). Plan-zero users still see
   * the tab in its locked/upgrade state — only this flag hides it entirely.
   *
   * Controlled by NEXT_PUBLIC_LIVE_DATA_CONNECTORS for staged rollout.
   */
  LIVE_DATA_CONNECTORS: isEnabled(process.env.NEXT_PUBLIC_LIVE_DATA_CONNECTORS),

  /**
   * Bot Settings Workspace V2
   *
   * Enables the guided bot settings workspace for /bot/[id]/settings.
   *
   * Controlled by NEXT_PUBLIC_SETTINGS_WORKSPACE_V2 for staged rollout.
   */
  SETTINGS_WORKSPACE_V2:
    (process.env.NEXT_PUBLIC_SETTINGS_WORKSPACE_V2 || 'false').toLowerCase() === 'true',

  /**
   * Integrations Workspace V2
   *
   * Enables the guided integrations workspace.
   *
   * Controlled by NEXT_PUBLIC_INTEGRATIONS_WORKSPACE_V2 for staged rollout.
   */
  INTEGRATIONS_WORKSPACE_V2: isEnabled(process.env.NEXT_PUBLIC_INTEGRATIONS_WORKSPACE_V2),

  /**
   * Marketplace
   *
   * Enables the public marketplace browse/detail experience and marketplace
   * navigation entry points.
   *
   * Disabled while the feature is still in development — hides the sidebar
   * entry and 404s the /marketplace routes. Flip back to true to reveal.
   */
  MARKETPLACE: false,

  /**
   * Creator Studio
   *
   * Enables vendor onboarding, creator listing workspaces, and marketplace
   * listing management routes.
   *
   * Disabled while the feature is still in development — hides the sidebar
   * entries (incl. the super-admin "Marketplace bots" governance item) and
   * redirects the /creator routes to the dashboard. Flip back to true to reveal.
   */
  CREATOR_STUDIO: false,

  /**
   * Scheduled Knowledge
   *
   * Enables scheduled source management in bot and creator knowledge workspaces.
   *
   * Enabled in code so scheduled knowledge management is available
   * without requiring local environment edits.
   */
  KNOWLEDGE_SCHEDULER: true,

  /**
   * Document Studio
   *
   * Enables generated-document workflows for chatbot knowledge.
   *
   * Enabled in code so generated-document workflows are available
   * without requiring local environment edits.
   */
  DOCUMENT_STUDIO: true,

  /** Owner MCP Tools workspace; disabled until the API rollout is approved. */
  MCP_CONNECTORS_V2: isEnabled(process.env.NEXT_PUBLIC_MCP_CONNECTORS_V2),

  /**
   * Invoice Intake workspace — internal.
   *
   * Off unless switched on, and gated to SUPER_ADMIN on top of this flag. Intake is a
   * route into a client's books, so it is hidden from customers entirely rather than
   * shown and refused.
   */
  EMAIL_INTAKE: isEnabled(process.env.NEXT_PUBLIC_EMAIL_INTAKE),

  /**
   * Atlas home (BODR). The chat-first /home workspace. Off by default; rollout goes internal accounts ->
   * pilot -> cohorts (PLAN §9). While off, /home is a 404 and nothing in the classic app changes.
   * Server counterpart: ATLAS_AGENT_ENABLED (apps/api/src/atlas/atlas.config.ts).
   */
  ATLAS_HOME: isEnabled(process.env.NEXT_PUBLIC_ATLAS_HOME),

  /**
   * Account-wide expert knowledge toggles (tenant knowledge base, plan rev 4 D3). Shows the
   * owner-only "Account-wide expert knowledge" switch on each admin, staff and secret-API-key
   * row. Off by default: the grants it writes stay inert until the server's tenant knowledge
   * base is configured (TENANT_KB_KNOWLEDGE_BASE_ID), so the switch is hidden until then.
   */
  TENANT_KNOWLEDGE_GRANTS: isEnabled(process.env.NEXT_PUBLIC_TENANT_KNOWLEDGE_GRANTS),
};

/**
 * Get a feature flag value
 * @param flag - The feature flag key
 * @returns The feature flag value
 */
export function isFeatureEnabled(flag: keyof typeof FEATURE_FLAGS): boolean {
  return FEATURE_FLAGS[flag] ?? false;
}
